import stringify from 'fast-safe-stringify'

import { useStore } from '~/store/store'

import { AsyncFn, getErrorMessage } from './utils'

const onGoingFetches = new Map<string, Promise<void>>()

/**
 * Middleware/Decorator that adds the fetched entities to the store
 */
export function withImport<T extends AsyncFn>(call: T): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
    return async (...args: Parameters<T>) => {
        const [query] = args
        const key = `${call.name}__${stringify(query)}`

        if (onGoingFetches.has(key)) {
            return onGoingFetches.get(key)
        }

        const promise = call(...args)

        onGoingFetches.set(key, promise)

        // Error on fetch should be handled by the caller, so that multiple
        // fetch errors can be handled with a single user-facing error message
        let result
        try {
            result = await promise
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`${getErrorMessage(call, args, error.message)}`)
            } else {
                throw new Error(`Unknown error. ${getErrorMessage(call, args, error)}`)
            }
        } finally {
            onGoingFetches.delete(key)
        }

        if (!String(result?.response?.status).startsWith('2')) {
            throw new Error(`${getErrorMessage(call, args, result)}`)
        }

        useStore.getState().di.actions.addEntities(result.data)

        return result
    }
}
